import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { LeadService } from 'src/app/private/services/lead.service';
import { LeadActivityService } from 'src/app/private/services/lead-activity.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { buildCommunicationLogPayload, extractCommunicationLogId } from 'src/app/utils/communication-log.util';

@Component({
  selector: 'app-lead-whatsapp',
  templateUrl: './lead-whatsapp.component.html',
  styleUrls: ['./lead-whatsapp.component.scss'],
})
export class LeadWhatsappComponent implements OnInit {
  @Input() communicationData: any;
  @Output() close = new EventEmitter<void>();
  @Output() communicationLogged = new EventEmitter<void>();

  editingDetails = false;
  contactName = '';
  contactEmail = '';
  contactPhone = '';
  messageText = '';

  constructor(
    private toast: ToastrService,
    private api: LeadService,
    private localStorage: LocalStorageService,
    private leadActivity: LeadActivityService
  ) {}

  ngOnInit(): void {
    this.resetFromLead();
    this.messageText = this.buildDefaultMessage();
  }

  get displayName(): string {
    return (this.contactName || this.communicationData?.contact_name || 'Lead').trim();
  }

  toggleEditDetails(): void {
    this.editingDetails = !this.editingDetails;
  }

  closeModal(): void {
    this.close.emit();
  }

  sendViaWeb(): void {
    this.openWhatsapp('web');
  }

  sendViaApp(): void {
    this.openWhatsapp('app');
  }

  private resetFromLead(): void {
    const data = this.communicationData || {};
    const fields = data.fields || {};
    this.contactName = String(data.contact_name || fields.name || '').trim();
    this.contactEmail = String(
      data.contact_email || fields.email || fields.contact_email || ''
    ).trim();
    this.contactPhone = String(
      data.contact_phone || fields.phone || fields.mobile || ''
    ).trim();
  }

  private buildDefaultMessage(): string {
    const firstName = this.displayName.split(/\s+/)[0] || 'there';
    return `Hi ${firstName},

I hope this message finds you well. I wanted to reach out regarding your inquiry.

Please let me know if you have any questions.

Best regards,
Sales Team`;
  }

  private openWhatsapp(mode: 'web' | 'app'): void {
    const phone = this.normalizePhoneForWhatsapp(this.contactPhone);
    if (!phone) {
      this.toast.error('Please enter a valid phone number');
      return;
    }
    if (!this.messageText.trim()) {
      this.toast.error('WhatsApp message is empty');
      return;
    }

    const text = encodeURIComponent(this.messageText.trim());
    const url =
      mode === 'web'
        ? `https://web.whatsapp.com/send?phone=${phone}&text=${text}`
        : `https://api.whatsapp.com/send?phone=${phone}&text=${text}`;

    window.open(url, '_blank', 'noopener,noreferrer');
    this.saveCommunicationHistory(mode);
  }

  private saveCommunicationHistory(mode: 'web' | 'app'): void {
    const leadId = this.communicationData?.lead_id;
    if (!leadId) {
      return;
    }

    const payload = buildCommunicationLogPayload(this.localStorage, {
      lead_id: leadId,
      communication_type: 'whatsapp',
      note: this.messageText.trim(),
      status: 'Initiated',
      email_extra_details: {
        to: this.contactPhone,
        contact_name: this.displayName,
        channel: mode,
        message: this.messageText.trim(),
      },
    });

    this.api.CreateCommunicationHisLog(payload).subscribe({
      next: (res: unknown) => {
        const logId = extractCommunicationLogId(res);
        if (logId) {
          this.leadActivity
            .recordCommunication(
              leadId,
              logId,
              'whatsapp',
              this.messageText.trim(),
              'Initiated'
            )
            .subscribe();
        }
        this.communicationLogged.emit();
      },
      error: (err) => console.error('Failed to save WhatsApp communication history', err),
    });
  }

  private normalizePhoneForWhatsapp(raw: string): string {
    const digits = String(raw || '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    if (digits.length === 10) {
      return `91${digits}`;
    }
    return digits;
  }
}
